import { useState, type ReactElement } from 'react';
import { Check, CheckCheck, Forward, MoreVertical } from 'lucide-react';
import { formatHoraSP } from '@fiodecobre/shared';
import { Avatar } from '../../../components/Avatar';
import { LightboxImagem } from './LightboxImagem';
import type { Contato, Mensagem, StatusMensagem } from '../lib/tipos';

const STATUS_ICON: Record<string, ReactElement> = {
  queued: <span className="text-gray-300 text-xs">...</span>,
  sent: <Check size={12} className="text-gray-400" />,
  delivered: <CheckCheck size={12} className="text-gray-400" />,
  read: <CheckCheck size={12} className="text-blue-500" />,
  failed: <span className="text-red-500 text-xs">!</span>,
};

function fmtHora(iso: string): string {
  // Backend grava criado_em como TIMESTAMP sem TZ com wall-clock SP — usa helper compartilhado pra corrigir.
  return formatHoraSP(iso);
}

interface Props {
  mensagens: Mensagem[];
  contato: Contato | null;
  onSelecionarTodas?: (mensagens: Mensagem[]) => void;
}

export function MensagemImagemGrupo({ mensagens, contato, onSelecionarTodas }: Props) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const ehSaida = mensagens[0]!.direcao === 'outbound';
  const ultima = mensagens[mensagens.length - 1]!;
  const total = mensagens.length;

  const imagens = mensagens.map((m) => ({ url: m.midia_url ?? '', legenda: m.texto }));

  // Mostra ate 4 thumbs em grid 2x2. Se total > 4, ultima ganha overlay "+N".
  const visiveis = mensagens.slice(0, 4);
  const restantes = total - 4;

  // Status agregado: ultimo do batch (se algum nao chegou em "read" ainda, mostra do mais atrasado).
  // Pra simplificar, exibe o status da ultima mensagem.
  const statusAgregado: StatusMensagem = ultima.status;

  // Legenda: primeira nao-vazia entre as imagens
  const legendaGrupo = mensagens.find((m) => m.texto && m.texto.trim().length > 0)?.texto ?? null;

  // Assinatura: pega a primeira nao-vazia (geralmente todas iguais no batch)
  const assinatura = mensagens.find((m) => m.assinatura)?.assinatura ?? null;

  return (
    <div id={`msg-grupo-${mensagens[0]!.id}`} className={`group flex mb-1.5 ${ehSaida ? 'justify-end' : 'justify-start'}`}>
      {!ehSaida && contato && (
        <div className="flex-shrink-0 self-end mb-1 mr-1.5">
          <Avatar nome={contato.nome || contato.push_name || ''} fotoUrl={contato.foto_url} tamanho={34} />
        </div>
      )}

      {onSelecionarTodas && (
        <div className={`flex items-center self-center opacity-0 group-hover:opacity-100 transition-opacity ${ehSaida ? 'order-first mr-1.5' : 'ml-1.5'}`}>
          <button
            onClick={() => onSelecionarTodas(mensagens)}
            className="p-1.5 rounded-full shadow-sm border border-yellow-600 hover:brightness-95 transition"
            style={{ backgroundColor: '#fde68a' }}
            title={`Selecionar as ${total} imagens deste grupo`}
          >
            <MoreVertical size={18} style={{ color: '#000' }} strokeWidth={2.5} />
          </button>
        </div>
      )}

      <div className="relative" style={{ maxWidth: 'min(85%, 380px)' }}>
        <div className={ehSaida ? 'bubble-out' : 'bubble-in'}>
          {ehSaida && assinatura && (
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="font-bold text-gray-700" style={{ fontSize: '19.2px' }}>{assinatura}</span>
            </div>
          )}
          {mensagens[0]!.encaminhada && (
            <div className="flex items-center gap-1 mb-0.5 text-gray-400">
              <Forward size={12} />
              <span className="text-[11px] italic">Encaminhada</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden" style={{ width: 280 }}>
            {visiveis.map((m, i) => {
              const eUltimaVisivel = i === visiveis.length - 1;
              const mostrarOverlay = eUltimaVisivel && restantes > 0;
              return (
                <div
                  key={m.id}
                  className="relative cursor-pointer bg-black/10 overflow-hidden"
                  style={{ aspectRatio: '1 / 1' }}
                  onClick={() => setLightboxIdx(i)}
                >
                  <img
                    id={`msg-${m.id}`}
                    src={m.midia_url ?? ''}
                    alt="Imagem"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  {mostrarOverlay && (
                    <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white text-2xl font-medium">
                      +{restantes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {legendaGrupo && (
            <p className="mt-1 text-sm whitespace-pre-line">{legendaGrupo}</p>
          )}

          <div className={`flex items-center gap-1 mt-0.5 ${ehSaida ? 'justify-end' : 'justify-start'}`}>
            <span className="text-[10px] text-gray-400">{total} fotos · {fmtHora(ultima.criado_em)}</span>
            {ehSaida && STATUS_ICON[statusAgregado]}
          </div>
        </div>
      </div>

      {lightboxIdx !== null && (
        <LightboxImagem
          imagens={imagens}
          indexInicial={lightboxIdx}
          onFechar={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}
